import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { setAdminSession, isAdminAuthenticated } from "../../utils/adminSession";
import { setAdminApiToken } from "../../utils/adminApiToken";
import { completeAdminSignIn } from "../../controllers/adminSignInController";
import { useAppDispatch } from "../../store/hooks";
import { pushToast } from "../../store/slices/toastSlice";
import { adminSignInFormResolver } from "./adminLoginFormResolver";
import VizhacksLogoMark from "../../components/VizhacksLogoMark";
import "./admin.css";

export default function AdminLogin() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { email: "", password: "" },
    resolver: adminSignInFormResolver,
    mode: "onSubmit",
  });

  const emailWatch = watch("email");
  const passwordWatch = watch("password");
  useEffect(() => {
    clearErrors("root");
  }, [emailWatch, passwordWatch, clearErrors]);

  if (isAdminAuthenticated()) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  /** @param {{ email: string, password: string }} data */
  async function onSubmitValid(data) {
    clearErrors("root");
    try {
      const result = await completeAdminSignIn({
        email: data.email,
        password: data.password,
      });
      if (result.ok) {
        setAdminSession();
        setAdminApiToken(result.token || "");
        if (result.usedFallback) {
          dispatch(
            pushToast({
              variant: "info",
              title: "Offline sign-in",
              message:
                "API unreachable — used static fallback. Start the backend for real auth.",
            })
          );
        }
        navigate("/admin/dashboard", { replace: true });
        return;
      }
      setError("root", {
        type: "server",
        message:
          result.errorMessage || "Incorrect email or password.",
      });
    } catch {
      setError("root", {
        type: "server",
        message: "Something went wrong. Try again.",
      });
    }
  }

  const topErrors = [];
  if (errors.root?.message) topErrors.push(errors.root.message);
  if (errors.email?.message) topErrors.push(errors.email.message);
  if (errors.password?.message) topErrors.push(errors.password.message);

  return (
    <div className="admin-root">
      <div className="admin-card">
        <div className="admin-brand">
          <VizhacksLogoMark
            className="admin-brand__logo"
            height={28}
            aria-label="Vizhacks"
          />
          <span className="admin-kicker">Admin</span>
          <h1 className="admin-title">Sign in</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmitValid)} noValidate>
          {topErrors.length > 0 ? (
            <div className="admin-form-errors-top" role="alert" aria-live="polite">
              <ul className="admin-form-errors-top__list">
                {topErrors.map((msg, i) => (
                  <li key={`${i}-${msg}`} className="admin-form-errors-top__item">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="admin-field">
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              placeholder="you@vizhacks.com"
              aria-invalid={errors.email ? "true" : "false"}
              className={errors.email ? "admin-input admin-input--invalid" : "admin-input"}
              {...register("email")}
            />
          </div>
          <div className="admin-field">
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.password ? "true" : "false"}
              className={
                errors.password ? "admin-input admin-input--invalid" : "admin-input"
              }
              {...register("password")}
            />
          </div>
          <button
            type="submit"
            className="admin-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
      <p className="admin-foot">
        <Link to="/">Back to site</Link>
      </p>
    </div>
  );
}
